import { inject } from '@angular/core';
import { CanActivateFn, Route, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { ChatComponent } from './components/chat/chat.component';
import { CollectionsComponent } from './components/collections/collections.component';
import { SettingsComponent } from './components/settings/settings.component';
import { ConnectionService, HasuraRole } from './services/connection.service';
import { ChatNewComponent } from './components/chat-new/chat-new.component';
import { OrganizationsComponent } from './components/settings/organizations/organizations.component';
import { OrganizationConfigComponent } from './components/settings/organizations/organization-config/organization-config.component';
import { OrganizationEditComponent } from './components/settings/organizations/organization-edit/organization-edit.component';
import { UsersComponent } from './components/settings/users/users.component';
import { UserEditComponent } from './components/settings/users/user-edit/user-edit.component';
import { AiModelsComponent } from './components/settings/ai-models/ai-models.component';
import { AiModelEditComponent } from './components/settings/ai-models/ai-model-edit/ai-model-edit.component';

/**
 * Custom canActivate function used to check if a user is a role
 * @param role Either a single role or array of allowed roles
 * @returns Promise true
 */
function isRole(role: HasuraRole | HasuraRole[]): CanActivateFn {
    // Compare role to first role emitted by UserService
    return async () => {
        const router = inject(Router);
        const userRole = await firstValueFrom(inject(ConnectionService).role.pipe((filter((x) => !!x))));
        const hasRole = typeof role === 'string' ? userRole === role : role.includes(userRole);
        return hasRole || router.parseUrl('/chat/new');
    };
}

export const routes: AppRoute[] = [
    {
        path: 'settings',
        component: SettingsComponent,
        data: { activeSidenavItem: 'settings' },
        children: [
            {
                path: '',
                redirectTo: '/settings/users',
                pathMatch: 'full',
            },
            {
                path: 'users',
                canActivate: [isRole(['admin', 'manager'])],
                children: [
                    {
                        path: '',
                        component: UsersComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin', 'manager'])],
                    },
                    {
                        path: 'new',
                        component: UserEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin', 'manager'])],
                    },
                    {
                        path: ':userId',
                        component: UserEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin', 'manager'])],
                    },
                ],
            },
            {
                path: 'organizations',
                canActivate: [isRole(['admin'])],
                children: [
                    {
                        path: '',
                        component: OrganizationsComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin', 'manager'])],
                    },
                    {
                        path: 'new',
                        component: OrganizationEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin'])],
                    },
                    {
                        path: ':organizationId',
                        component: OrganizationEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin'])],
                    },
                    {
                        path: 'config/:organizationId',
                        component: OrganizationConfigComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin'])],
                    },
                ],
            },
            {
                path: 'models',
                canActivate: [isRole(['admin'])],
                children: [
                    {
                        path: '',
                        component: AiModelsComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin', 'manager'])],
                    },
                    {
                        path: 'new',
                        component: AiModelEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin'])],
                    },
                    {
                        path: ':aiModelId',
                        component: AiModelEditComponent,
                        data: { activeSidenavItem: 'settings' },
                        canActivate: [isRole(['admin'])],
                    },
                ],
            },
        ],
    },
    {
        path: 'chat',
        data: {
            activeSidenavItem: 'chat',
        },
        children: [
            {
                path: '',
                component: ChatComponent,
                data: { activeSidenavItem: 'chat' },
            },
            {
                path: 'new',
                component: ChatNewComponent,
                data: { activeSidenavItem: 'chat' },
            },
            {
                path: ':chatId',
                component: ChatComponent,
                data: { activeSidenavItem: 'chat' },
            },
        ],
    },
    {
        path: 'collections',
        data: {
            activeSidenavItem: 'collections',
        },
        children: [
            {
                path: '',
                component: CollectionsComponent,
                data: { activeSidenavItem: 'collections' },
            },
            {
                path: 'new',
                component: CollectionsComponent,
                data: { activeSidenavItem: 'collections' },
            },
            {
                path: ':collectionId',
                data: { activeSidenavItem: 'collections' },
                children: [
                    {
                        path: '',
                        component: CollectionsComponent,
                        data: { activeSidenavItem: 'collections' },
                    },
                    {
                        path: 'edit',
                        component: CollectionsComponent,
                        data: { activeSidenavItem: 'collections' },
                        canActivate: [isRole(['admin', 'manager', 'editor'])],
                        canDeactivate: [
                            (component: CollectionsComponent): boolean | Promise<boolean> => component.canDeactivate(),
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '**',
        redirectTo: '/chat/new',
    },
];

/**
 * Custom defintion of an app route that includes option for active sidenav
 */
export type AppRoute = Route & {
    children?: AppRoute[];
    data?: {
        activeSidenavItem?: 'chat' | 'collections' | 'organizations' | 'settings';
    };
};
